// Phase 14e-b — Facility detail Invoices section.
//
// Per 14e-b-4 lock: between Users + Audit trail. Renders paginated
// AdminTable of GET /admin/invoices?facilityId={id}. Row hosts 7 action
// buttons per the 14e-4 tier matrix (issue / void / regenerate /
// record-payment / waive-interest / skip-next-dunning / refund).
// Click row → opens inline <InvoiceDetailModal> with lines + paymentEvents.

import { useRef, useState, type ReactNode } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Alert, Button, Card, CardHeader, Field, Input, useFocusTrap } from '@truecare/ui';
import { messageOf, unwrap } from '@truecare/api-client';
import { api } from '../api';
import { AdminTable, type AdminTableColumn } from './AdminTable';
import { DangerButton } from './DangerButton';
import { OverrideActionButton } from './OverrideActionButton';

interface InvoiceRow {
  id: string;
  invoiceNumber: string | null;
  status: string;
  totalCents: number;
  cycleStartDate: string;
  cycleEndDate: string;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  voidedAt: string | null;
  amountPaidCents: number;
}

interface InvoiceDetail extends InvoiceRow {
  /** Short-TTL signed download URL for the rendered invoice PDF; null until the
   *  render-invoice-pdf job has run (DRAFT invoices + the brief async window). */
  pdfUrl: string | null;
  lines: {
    id: string;
    description: string;
    quantity: number;
    unitCents: number;
    lineTotalCents: number;
  }[];
  paymentEvents: {
    id: string;
    eventType: string;
    amountCents: number;
    externalRef: string | null;
    recordedAt: string;
  }[];
}

export function InvoicesCard({ facilityId }: { facilityId: string }) {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [detailId, setDetailId] = useState<string | null>(null);

  const queryKey = ['admin', 'invoices', { facilityId, page, limit }] as const;
  const listQuery = useQuery({
    queryKey,
    queryFn: async () => {
       
      const r = (await unwrap(
        api.GET('/api/v1/admin/invoices' as never, {
          params: {
            query: {
              facilityId,
              page: String(page),
              pageSize: String(limit),
            },
          },
        } as never),
      )) as { items: InvoiceRow[]; totalCount: number };
      return r;
    },
  });

  function invalidate(): void {
    void qc.invalidateQueries({ queryKey: ['admin', 'invoices'] });
  }

  const columns: AdminTableColumn<InvoiceRow>[] = [
    {
      key: 'invoiceNumber',
      header: 'Number',
      width: 'w-40',
      render: (r) => r.invoiceNumber ?? 'DRAFT',
    },
    { key: 'status', header: 'Status', width: 'w-28' },
    {
      key: 'cycleStartDate',
      header: 'Cycle',
      width: 'w-44',
      render: (r) => `${r.cycleStartDate} → ${r.cycleEndDate}`,
    },
    {
      key: 'totalCents',
      header: 'Total',
      width: 'w-28',
      render: (r) => formatCents(r.totalCents),
    },
    {
      key: 'issuedAt',
      header: 'Issued',
      width: 'w-32',
      render: (r) => (r.issuedAt ? r.issuedAt.slice(0, 10) : '—'),
    },
    {
      key: '__actions',
      header: 'Actions',
      sortable: false,
      width: 'w-auto',
      render: (r) => (
        <InvoiceRowActions
          invoice={r}
          facilityId={facilityId}
          onMutation={invalidate}
        />
      ),
    },
  ];

  return (
    <Card padding="md" className="mt-6" data-testid="facility-invoices-card">
      <CardHeader title="Invoices" />
      <div className="mt-4">
        <AdminTable
          columns={columns}
          rows={listQuery.data?.items ?? []}
          isLoading={listQuery.isPending}
          isError={listQuery.isError}
          emptyMessage="No invoices for this facility yet."
          page={page}
          limit={limit}
          totalCount={listQuery.data?.totalCount ?? 0}
          onPageChange={setPage}
          onLimitChange={(n) => {
            // 2026-07-23 audit (U4): resetting to page 1 is required — the
            // backends compute skip=(page-1)*pageSize with no clamping, so
            // growing the page size while on page 2+ can put skip past
            // totalCount and render "no results" over a populated list.
            setLimit(n);
            setPage(1);
          }}
          onRowClick={(r) => setDetailId(r.id)}
        />
      </div>

      {detailId && (
        <InvoiceDetailModal
          invoiceId={detailId}
          onClose={() => setDetailId(null)}
        />
      )}
    </Card>
  );
}

// ─── Row actions ────────────────────────────────────────────────────────────

function InvoiceRowActions({
  invoice,
  facilityId,
  onMutation,
}: {
  invoice: InvoiceRow;
  facilityId: string;
  onMutation: () => void;
}) {
  // Mutation helpers (each maps to one of the 8 backend endpoints
  // including force-cycle-close per 14e-c-6 lock).
  const issue = useMutation({
     
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/admin/invoices/{id}/issue' as never, {
          params: { path: { id: invoice.id } },
          body: {},
        } as never),
      ),
    onSuccess: onMutation,
    // Issue is fired from a window.confirm (no inline modal to display
    // errors). Surface failures via window.alert so the operator
    // actually sees them. messageOf() reads the backend's real
    // Convention-03 reason (error.body.message) — e.message alone is the
    // generic synthesized "API 4xx".
    onError: (e) => {
      window.alert(`Failed to issue invoice: ${messageOf(e, 'unknown error')}`);
    },
  });
  const regenerate = useMutation({
    mutationFn: (body: { reason: string }) =>
       
      unwrap(
        api.POST('/api/v1/admin/invoices/{id}/regenerate' as never, {
          params: { path: { id: invoice.id } },
          body,
        } as never),
      ),
    onSuccess: onMutation,
  });
  const voidMut = useMutation({
    mutationFn: (body: { reason: string }) =>
       
      unwrap(
        api.POST('/api/v1/admin/invoices/{id}/void' as never, {
          params: { path: { id: invoice.id } },
          body,
        } as never),
      ),
    onSuccess: onMutation,
  });
  const recordPayment = useMutation({
     
    mutationFn: (body: Record<string, unknown>) =>
      unwrap(
        api.POST('/api/v1/admin/invoices/{id}/record-payment' as never, {
          params: { path: { id: invoice.id } },
          body,
        } as never),
      ),
    onSuccess: onMutation,
  });
  const waiveInterest = useMutation({
     
    mutationFn: (body: { period: string; reason: string }) =>
      unwrap(
        api.POST('/api/v1/admin/invoices/{id}/waive-interest' as never, {
          params: { path: { id: invoice.id } },
          body,
        } as never),
      ),
    onSuccess: onMutation,
  });
  const skipDunning = useMutation({
    mutationFn: (body: { reason: string }) =>
       
      unwrap(
        api.POST('/api/v1/admin/invoices/{id}/skip-next-dunning' as never, {
          params: { path: { id: invoice.id } },
          body,
        } as never),
      ),
    onSuccess: onMutation,
  });
  const refund = useMutation({
    mutationFn: (body: { invoiceId: string; amountCents: number; reason: string }) =>
       
      unwrap(api.POST('/api/v1/admin/payment-events/refund' as never, { body } as never)),
    onSuccess: onMutation,
  });
  // Phase 14e-c-6 — 8th button. Force-close the cycle this invoice
  // belongs to; backend creates a corrected DRAFT for the same
  // (facilityId, cycleStartDate). Idempotent: backend skip-returns
  // when a non-voided invoice already exists for the cycle.
  const forceCycleClose = useMutation({
    mutationFn: (body: { facilityId: string; cycleStartDate: string; reason: string }) =>
       
      unwrap(
        api.POST('/api/v1/admin/invoices/force-cycle-close' as never, {
          body,
        } as never),
      ),
    onSuccess: onMutation,
  });

  const isDraft = invoice.status === 'DRAFT';
  const isVoided = invoice.status === 'VOIDED';
  const isPaid = invoice.status === 'PAID';
  const isIssued = !isDraft && !isVoided;

  // Hooks for record-payment + waive-interest captured by closures
  // since OverrideActionButton's extraFields gives us inputs but reads
  // happen here at mutate time.
  const [paymentMechanic, setPaymentMechanic] = useState<string>('CHEQUE');
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [paymentRef, setPaymentRef] = useState<string>('');
  const [waivePeriod, setWaivePeriod] = useState<string>('');
  const [refundAmount, setRefundAmount] = useState<string>('');

  return (
    <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
      {isDraft && (
        <Button
          size="sm"
          variant="primary"
          onClick={() => {
            if (window.confirm(`Issue invoice ${invoice.invoiceNumber ?? '(DRAFT)'}?`)) {
              issue.mutate();
            }
          }}
          loading={issue.isPending}
          data-testid={`invoice-issue-${invoice.id}`}
        >
          Issue
        </Button>
      )}
      {isDraft && (
        <OverrideActionButton
          actionLabel="Regenerate invoice"
          onConfirm={({ reason }) => regenerate.mutateAsync({ reason })}
          loading={regenerate.isPending}
          data-testid={`invoice-regenerate-${invoice.id}`}
        >
          Regenerate
        </OverrideActionButton>
      )}
      {!isVoided && (
        <DangerButton
          entityName={invoice.invoiceNumber ?? 'DRAFT'}
          actionLabel="Void invoice"
          confirmationToken={invoice.invoiceNumber ?? 'DRAFT'}
          onConfirm={({ reason }) => voidMut.mutateAsync({ reason: reason ?? '' })}
          loading={voidMut.isPending}
          data-testid={`invoice-void-${invoice.id}`}
        >
          Void
        </DangerButton>
      )}
      {isIssued && !isPaid && (
        <OverrideActionButton
          actionLabel="Record payment"
          reasonLabel="Operator note"
          instructions="Record an out-of-band payment (cheque / EFT / wire / PAD). Amount is in dollars."
          extraFields={
            <>
              <Field label="Mechanic">
                <select
                  value={paymentMechanic}
                  onChange={(e) => setPaymentMechanic(e.target.value)}
                  className="w-full rounded-md border border-border bg-cream p-2 text-sm"
                  data-testid={`payment-mechanic-${invoice.id}`}
                >
                  <option value="CHEQUE">Cheque</option>
                  <option value="EFT">EFT</option>
                  <option value="WIRE">Wire</option>
                  <option value="PAD">PAD</option>
                  <option value="MANUAL_CREDIT">Manual credit</option>
                </select>
              </Field>
              <Field label="Amount ($)">
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  data-testid={`payment-amount-${invoice.id}`}
                />
              </Field>
              <Field label="Reference (cheque # / EFT trace)">
                <Input
                  type="text"
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                  data-testid={`payment-ref-${invoice.id}`}
                />
              </Field>
            </>
          }
          onConfirm={async ({ reason }) => {
            // Pre-mutate validation — these fields are required per
            // RecordFacilityPaymentRequestSchema. Throwing here keeps
            // the modal open + surfaces the error inline so the
            // operator can correct without losing typed input.
            const cents = Math.round(parseFloat(paymentAmount || '0') * 100);
            if (!Number.isFinite(cents) || cents <= 0) {
              throw new Error('Amount must be a positive dollar value.');
            }
            if (!paymentRef.trim()) {
              throw new Error('Reference is required — cheque # / EFT trace.');
            }
            await recordPayment.mutateAsync({
              mechanic: paymentMechanic,
              amountCents: cents,
              externalRef: paymentRef.trim(),
              operatorNote: reason,
            });
            setPaymentAmount('');
            setPaymentRef('');
          }}
          loading={recordPayment.isPending}
          data-testid={`invoice-record-payment-${invoice.id}`}
        >
          Record payment
        </OverrideActionButton>
      )}
      {isIssued && (
        <OverrideActionButton
          actionLabel="Waive interest"
          extraFields={
            <Field label="Period (YYYY-MM)">
              <Input
                type="text"
                placeholder="2026-06"
                pattern="\d{4}-\d{2}"
                value={waivePeriod}
                onChange={(e) => setWaivePeriod(e.target.value)}
                data-testid={`waive-period-${invoice.id}`}
              />
            </Field>
          }
          onConfirm={async ({ reason }) => {
            if (!/^\d{4}-\d{2}$/.test(waivePeriod)) {
              throw new Error('Period must be in YYYY-MM format (e.g. 2026-06).');
            }
            await waiveInterest.mutateAsync({ period: waivePeriod, reason });
            setWaivePeriod('');
          }}
          loading={waiveInterest.isPending}
          data-testid={`invoice-waive-interest-${invoice.id}`}
        >
          Waive interest
        </OverrideActionButton>
      )}
      {isIssued && (
        <OverrideActionButton
          actionLabel="Skip next dunning step"
          onConfirm={({ reason }) => skipDunning.mutateAsync({ reason })}
          loading={skipDunning.isPending}
          data-testid={`invoice-skip-dunning-${invoice.id}`}
        >
          Skip dunning
        </OverrideActionButton>
      )}
      {(isPaid || invoice.amountPaidCents > 0) && (
        <DangerButton
          entityName={invoice.invoiceNumber ?? 'DRAFT'}
          actionLabel="Refund payment"
          confirmationToken="REFUND"
          extraFields={
            <Field label="Refund amount ($)">
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                data-testid={`refund-amount-${invoice.id}`}
              />
            </Field>
          }
          onConfirm={async ({ reason }) => {
            const cents = Math.round(parseFloat(refundAmount || '0') * 100);
            if (!Number.isFinite(cents) || cents <= 0) {
              throw new Error('Refund amount must be a positive dollar value.');
            }
            await refund.mutateAsync({
              invoiceId: invoice.id,
              amountCents: cents,
              reason: reason ?? '',
            });
            setRefundAmount('');
          }}
          loading={refund.isPending}
          data-testid={`invoice-refund-${invoice.id}`}
        >
          Refund
        </DangerButton>
      )}
      {/* Phase 14e-c-6 — force-cycle-close on VOIDED rows. Post-void
       *  recovery: creates a corrected DRAFT for the same cycle. */}
      {isVoided && (
        <OverrideActionButton
          actionLabel="Force-close this cycle"
          reasonLabel="Reason"
          instructions="Creates a corrected DRAFT invoice for the same cycle. Use after voiding an issued invoice. Backend is idempotent: if a non-voided invoice already exists for this cycle, the call is a no-op."
          onConfirm={async ({ reason }) => {
            await forceCycleClose.mutateAsync({
              facilityId,
              cycleStartDate: invoice.cycleStartDate,
              reason,
            });
          }}
          loading={forceCycleClose.isPending}
          data-testid={`invoice-force-cycle-close-${invoice.id}`}
        >
          Force-close cycle
        </OverrideActionButton>
      )}
    </div>
  );
}

// ─── Inline detail modal ────────────────────────────────────────────────────

function InvoiceDetailModal({
  invoiceId,
  onClose,
}: {
  invoiceId: string;
  onClose: () => void;
}) {
  const detailQuery = useQuery({
    queryKey: ['admin', 'invoices', invoiceId] as const,
    queryFn: async () => {
       
      const r = (await unwrap(
        api.GET('/api/v1/admin/invoices/{id}' as never, {
          params: { path: { id: invoiceId } },
        } as never),
      )) as InvoiceDetail;
      return r;
    },
  });

  // Re-render recovery (audit 2026-09-25) — shown when an issued invoice has no PDF yet
  // (render failed all retries, or still rendering). POSTs re-render-pdf + refetches.
  const reRender = useMutation({
    mutationFn: async () =>
      unwrap(
        api.POST('/api/v1/admin/invoices/{id}/re-render-pdf' as never, {
          params: { path: { id: invoiceId } },
        } as never),
      ),
    onSuccess: () => {
      void detailQuery.refetch();
    },
  });

  // Focus in, trap Tab, Escape-to-close, restore focus on unmount (H-F4).
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, { onEscape: onClose });

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="invoice-detail-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      data-testid="invoice-detail-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-3xl rounded-md border border-border bg-cream p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <h2
            id="invoice-detail-modal-title"
            className="font-heading text-xl text-forest"
          >
            Invoice detail
          </h2>
          <div className="flex items-center gap-2">
            {detailQuery.data?.pdfUrl ? (
              <a
                href={detailQuery.data.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md border border-forest px-3 py-1 text-sm font-medium text-forest hover:bg-forest hover:text-cream"
                data-testid="invoice-download-pdf"
              >
                Download PDF
              </a>
            ) : detailQuery.data?.invoiceNumber ? (
              <Button
                size="sm"
                variant="secondary"
                loading={reRender.isPending}
                onClick={() => reRender.mutate()}
                data-testid="invoice-rerender-pdf"
              >
                Re-render PDF
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>

        {detailQuery.isPending && <p className="mt-4 text-sm">Loading…</p>}
        {detailQuery.isError && (
          <Alert tone="danger" className="mt-3">
            {messageOf(detailQuery.error, 'Failed to load invoice detail.')}
          </Alert>
        )}
        {detailQuery.data && (
          <div className="mt-4 space-y-6">
            <Summary detail={detailQuery.data} />
            <Lines lines={detailQuery.data.lines} />
            <PaymentEvents events={detailQuery.data.paymentEvents} />
          </div>
        )}
      </div>
    </div>
  );
}

function Summary({ detail }: { detail: InvoiceDetail }) {
  return (
    <dl className="grid grid-cols-2 gap-3 text-sm">
      <Field2 label="Number" value={detail.invoiceNumber ?? 'DRAFT'} />
      <Field2 label="Status" value={detail.status} />
      <Field2 label="Cycle" value={`${detail.cycleStartDate} → ${detail.cycleEndDate}`} />
      <Field2 label="Total" value={formatCents(detail.totalCents)} />
      <Field2 label="Amount paid" value={formatCents(detail.amountPaidCents)} />
      <Field2 label="Issued at" value={detail.issuedAt ?? '—'} />
    </dl>
  );
}

function Field2({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ink-muted">{label}</dt>
      <dd className="mt-1">{value}</dd>
    </div>
  );
}

function Lines({ lines }: { lines: InvoiceDetail['lines'] }) {
  return (
    <section>
      <h3 className="font-heading text-sm uppercase tracking-wide text-forest mb-2">
        Lines ({lines.length})
      </h3>
      {lines.length === 0 ? (
        <p className="text-sm text-ink-muted">No lines.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-ink/5 text-left">
            <tr>
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2 w-20">Qty</th>
              <th className="px-3 py-2 w-28">Unit</th>
              <th className="px-3 py-2 w-28">Total</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id} className="border-t border-border">
                <td className="px-3 py-2">{l.description}</td>
                <td className="px-3 py-2">{l.quantity}</td>
                <td className="px-3 py-2">{formatCents(l.unitCents)}</td>
                <td className="px-3 py-2">{formatCents(l.lineTotalCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function PaymentEvents({
  events,
}: {
  events: InvoiceDetail['paymentEvents'];
}) {
  return (
    <section>
      <h3 className="font-heading text-sm uppercase tracking-wide text-forest mb-2">
        Payment events ({events.length})
      </h3>
      {events.length === 0 ? (
        <p className="text-sm text-ink-muted">No payment events yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-ink/5 text-left">
            <tr>
              <th className="px-3 py-2 w-32">Type</th>
              <th className="px-3 py-2 w-28">Amount</th>
              <th className="px-3 py-2 w-36">Reference</th>
              <th className="px-3 py-2 w-44">Recorded at</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id} className="border-t border-border">
                <td className="px-3 py-2">{e.eventType}</td>
                <td className="px-3 py-2">{formatCents(e.amountCents)}</td>
                <td className="px-3 py-2">{e.externalRef ?? '—'}</td>
                <td className="px-3 py-2">{e.recordedAt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function formatCents(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}$${(abs / 100).toFixed(2)}`;
}
